import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Product } from "@repo/shared-types";
import { ApiClientError } from "@repo/api-client";
import { useAuth } from "../lib/auth-context";

export function ProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"price_asc" | "price_desc" | "name_asc" | "date_desc">("date_desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [addingId, setAddingId] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.listProducts({ limit: "20", sort, search: query || undefined })
      .then((res) => setProducts(res.products))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [query, sort]);

  async function handleAddToCart(productId: number) {
    if (!user) {
      setNotice("Sign in to add items to your cart.");
      navigate("/login");
      return;
    }

    try {
      setAddingId(productId);
      setNotice("");
      await api.addToCart(productId, 1);
      window.dispatchEvent(new Event("cart:updated"));
      setNotice("Item added to cart.");
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        setNotice("Sign in to add items to your cart.");
        navigate("/login");
        return;
      }
      setNotice(err instanceof Error ? err.message : "Failed to add item to cart");
    } finally {
      setAddingId(null);
    }
  }

  if (loading) return <div className="page-loading">Loading product catalog…</div>;

  return (
    <>
      {/* ── Page Hero ─────────────────────────────────────────── */}
      <section className="page-hero">
        <div className="container">
          <span className="eyebrow">Sacred Commerce</span>
          <h1 className="display-2 page-hero__title-spaced">
            Products &amp; membership passes
          </h1>
          <p className="body-lg text-muted page-hero__body">
            Browse our curated catalog of spiritual products, online courses, and membership passes —
            all synced live from the store.
          </p>

          {/* Filter bar */}
          <div className="search-bar search-bar--narrow">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products and passes…"
              className="form-input"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              className="form-input form-input--catalog-select"
            >
              <option value="date_desc">Newest first</option>
              <option value="name_asc">Name A–Z</option>
              <option value="price_asc">Price: low to high</option>
              <option value="price_desc">Price: high to low</option>
            </select>
          </div>

          <div className="shop-toolbar">
            <p className="body-sm text-muted shop-toolbar__hint">
              {user ? "Your cart syncs securely with your account." : "Sign in when you're ready to build a cart and checkout."}
            </p>
            <Link to={user ? "/cart" : "/login"} className="btn btn--outline btn--sm">
              {user ? "Open cart" : "Sign in to purchase"}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Products grid ─────────────────────────────────────── */}
      <section className="section section--surface-alt">
        <div className="container">
          {error  && <p className="alert alert--error alert--spaced">{error}</p>}
          {notice && <p className="alert alert--success alert--spaced">{notice}</p>}

          {products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">🛍️</div>
              <h3 className="heading-lg empty-state__title">No products found</h3>
              <p className="text-muted body-sm">
                {query ? `No results for "${query}". Try a different search.` : "Connect your store or adjust filters."}
              </p>
            </div>
          ) : (
            <>
              <p className="text-muted body-sm section-count-row">
                {products.length} product{products.length !== 1 ? "s" : ""} available
              </p>
              <div className="grid-auto">
                {products.map((product) => {
                  const canOrder = product.active && product.available_for_order !== false;
                  const stockLabel = product.quantity > 0
                    ? `${product.quantity} left`
                    : canOrder
                      ? "Available to order"
                      : "Sold out";
                  const preview = stripHtml(product.description_short || product.description).slice(0, 150) || "Sacred spiritual product.";
                  const imageUrl = product.default_image_url ?? product.images[0]?.url;

                  return (
                    <article key={product.id} className="product-card">
                      <div className="product-card__image">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={product.name}
                            loading="lazy"
                            className="product-card__image-el"
                          />
                        ) : (
                          <span className="product-card__image-fallback" aria-hidden="true">🕉️</span>
                        )}
                      </div>
                      <div className="product-card__body">
                        <div className="product-card__head">
                          <div>
                            <h3 className="product-card__title">{product.name}</h3>
                            {product.reference && <p className="product-card__sku">Ref {product.reference}</p>}
                          </div>
                          <span className={`badge ${product.quantity > 0 || canOrder ? "badge--green" : "badge--red"} badge--no-shrink`}>
                            {stockLabel}
                          </span>
                        </div>
                        <p className="product-card__desc">
                          {preview}
                        </p>
                        <div className="product-card__meta-row">
                          <span className="product-card__meta-chip">
                            {product.slug?.includes("poster") ? "Wall art" : product.slug?.includes("mug") ? "Ritual essential" : product.slug?.includes("notebook") ? "Journaling" : "Store item"}
                          </span>
                          {product.date_add && (
                            <span className="product-card__meta-text">
                              Added {new Date(product.date_add).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </span>
                          )}
                        </div>
                        <div className="product-card__footer">
                          <span className="product-card__price">${formatPrice(product.price)}</span>
                          <button
                            type="button"
                            className={canOrder ? "btn btn--primary btn--sm" : "btn btn--outline btn--sm"}
                            onClick={() => handleAddToCart(product.id)}
                            disabled={!canOrder || addingId === product.id}
                          >
                            {addingId === product.id ? "Adding…" : canOrder ? "Add to cart" : "Unavailable"}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function stripHtml(input: string): string {
  return input.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

function formatPrice(input: string): string {
  const value = Number(input);
  if (Number.isNaN(value)) return input;
  return value.toFixed(2);
}
